import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { t } from '../../utils/localization/localization';
// LANGUAGES import removed - unused
import { useLanguage } from '../../contexts/LanguageContext';
import { logger } from '../../utils/logger';
import { useLanguageAwareRender } from '../../hooks/useLanguageAwareRender';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Avatar,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tab,
  Tabs,
  Card,
  CardContent
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import WorkIcon from '@mui/icons-material/Work';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PeopleIcon from '@mui/icons-material/People';

export default function Profile() {
  const { currentUser, userRole, oidcLogoutRedirect, ROLES, getUserManager, getUserSubordinates } = useAuth();
  const { currentLanguage: _currentLanguage } = useLanguage();
  const [, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [manager, setManager] = useState(null);
  const [subordinates, setSubordinates] = useState([]);

  // Use standardized language-aware rendering hook
  const languageRenderKey = useLanguageAwareRender('Profile');

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Load manager and subordinates
  useEffect(() => {
    const loadUserHierarchy = async () => {
      if (currentUser) {
        try {
          // Make sure to await the async functions
          const userManager = await getUserManager(currentUser.id);
          const userSubordinates = await getUserSubordinates(currentUser.id);

          logger.info('[PROFILE] Loaded manager:', userManager);
          logger.info('[PROFILE] Loaded subordinates:', userSubordinates);

          setManager(userManager);
          setSubordinates(userSubordinates || []);
        } catch (error) {
          logger.error('[PROFILE] Error loading user hierarchy:', error);
        }
      }
    };

    loadUserHierarchy();
  }, [currentUser, getUserManager, getUserSubordinates]);

  // Get role display name
  const getRoleDisplayName = (role) => {
    switch(role) {
      case ROLES.ADMIN:
        return t('userProfile.roleAdmin');
      case ROLES.MANAGER:
        return t('userProfile.roleManager');
      case ROLES.USER:
        return t('userProfile.roleUser');
      default:
        return t('userProfile.roleUnknown');
    }
  };

  // Get role color
  const getRoleColor = (role) => {
    switch(role) {
      case ROLES.ADMIN:
        return 'error';
      case ROLES.MANAGER:
        return 'warning';
      case ROLES.USER:
        return 'success';
      default:
        return 'default';
    }
  };

  // Get permissions based on role
  const getPermissions = (role) => {
    const permissions = {
      [ROLES.USER]: [
        t('userProfile.permViewOwnAnalyses'),
        t('userProfile.permCreateCVAnalysis'),
        t('userProfile.permViewPublicReports')
      ],
      [ROLES.MANAGER]: [
        t('userProfile.permViewOwnAnalyses'),
        t('userProfile.permCreateCVAnalysis'),
        t('userProfile.permCreateJobAnalysis'),
        t('userProfile.permViewAllAnalyses'),
        t('userProfile.permCreateCompanyResearch'),
        t('userProfile.permViewAnalytics'),
        t('userProfile.permManageUsers')
      ],
      [ROLES.ADMIN]: [
        t('userProfile.permViewOwnAnalyses'),
        t('userProfile.permCreateCVAnalysis'),
        t('userProfile.permCreateJobAnalysis'),
        t('userProfile.permViewAllAnalyses'),
        t('userProfile.permCreateCompanyResearch'),
        t('userProfile.permViewAnalytics'),
        t('userProfile.permManageUsers'),
        t('userProfile.permManageRoles'),
        t('userProfile.permSystemConfig'),
        t('userProfile.permGenerateInvitationCodes')
      ]
    };

    return permissions[role] || [];
  };

  async function handleLogout() {
    try {
      setError('');
      await oidcLogoutRedirect();
    } catch (error) {
      setError('Failed to log out: ' + error.message);
    }
  }

  // Generate initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <Container maxWidth="md" key={languageRenderKey}>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: getRoleColor(userRole),
                  fontSize: '2.5rem',
                  mb: 2
                }}
              >
                {getInitials(currentUser?.name)}
              </Avatar>
              <Typography variant="h5" gutterBottom>
                {currentUser?.name}
              </Typography>
              <Chip
                label={getRoleDisplayName(userRole)}
                color={getRoleColor(userRole)}
                sx={{ mb: 2 }}
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={handleLogout}
                sx={{ mt: 2 }}
              >
                {t('userProfile.logOut')}
              </Button>
            </Grid>

            <Grid item xs={12} md={8}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="profile tabs">
                  <Tab icon={<PersonIcon />} label={t('userProfile.tabProfile')} />
                  <Tab icon={<PeopleIcon />} label={t('userProfile.tabTeam')} />
                  <Tab icon={<LockIcon />} label={t('userProfile.tabSecurity')} />
                </Tabs>
              </Box>

              {activeTab === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('userProfile.userInformation')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText primary={t('userProfile.name')} secondary={currentUser?.name} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <EmailIcon />
                      </ListItemIcon>
                      <ListItemText primary={t('userProfile.email')} secondary={currentUser?.email} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <WorkIcon />
                      </ListItemIcon>
                      <ListItemText primary={t('userProfile.role')} secondary={getRoleDisplayName(userRole)} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <SupervisorAccountIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('userProfile.manager')}
                        secondary={manager ? manager.name : t('userProfile.none')}
                      />
                    </ListItem>
                  </List>

                  {subordinates.length > 0 && (
                    <>
                      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        {t('userProfile.teamMembers')}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        {subordinates.map(subordinate => (
                          <Grid item xs={12} sm={6} key={subordinate.id}>
                            <Card variant="outlined">
                              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                  sx={{
                                    bgcolor: getRoleColor(subordinate.role),
                                    mr: 2
                                  }}
                                >
                                  {getInitials(subordinate.name)}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle1">
                                    {subordinate.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {subordinate.email}
                                  </Typography>
                                  <Chip
                                    label={getRoleDisplayName(subordinate.role)}
                                    color={getRoleColor(subordinate.role)}
                                    size="small"
                                    sx={{ mt: 1 }}
                                  />
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}

                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    {t('userProfile.permissions')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <List dense>
                    {getPermissions(userRole).map((permission, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <SecurityIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={permission} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {activeTab === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('userProfile.teamStructure')}
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {manager && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        {t('userProfile.yourManager')}
                      </Typography>
                      <Card variant="outlined">
                        <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar
                            sx={{
                              bgcolor: getRoleColor(manager.role),
                              width: 60,
                              height: 60,
                              mr: 2
                            }}
                          >
                            {getInitials(manager.name)}
                          </Avatar>
                          <Box>
                            <Typography variant="h6">
                              {manager.name}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                              {manager.email}
                            </Typography>
                            <Chip
                              label={getRoleDisplayName(manager.role)}
                              color={getRoleColor(manager.role)}
                              sx={{ mt: 1 }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  )}

                  <Typography variant="subtitle1" gutterBottom>
                    {subordinates.length > 0 ? t('userProfile.yourTeamMembers') : t('userProfile.noTeamMembers')}
                  </Typography>

                  {subordinates.length > 0 ? (
                    <Grid container spacing={2}>
                      {subordinates.map(subordinate => (
                        <Grid item xs={12} sm={6} md={4} key={subordinate.id}>
                          <Card variant="outlined">
                            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar
                                sx={{
                                  bgcolor: getRoleColor(subordinate.role),
                                  mr: 2
                                }}
                              >
                                {getInitials(subordinate.name)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1">
                                  {subordinate.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {subordinate.email}
                                </Typography>
                                <Chip
                                  label={getRoleDisplayName(subordinate.role)}
                                  color={getRoleColor(subordinate.role)}
                                  size="small"
                                  sx={{ mt: 1 }}
                                />
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      {t('userProfile.teamMembersWillAppear')}
                    </Typography>
                  )}
                </Box>
              )}

              {activeTab === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('userProfile.tabSecurity')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    {t(
                      'auth.oidcPasswordManagedByProvider',
                      'Password and account security are managed by the identity provider (Keycloak).'
                    )}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
}
